// All skin audio (Ancient Social profile song + Click Wheel clicks) runs through ONE
// Web Audio context and ONE global mute. 100% synthesized — no audio files, no
// licensed or trademarked sounds. Mute is persisted (localStorage) so it survives
// screens, skin switches and full reloads; muting suspends the context so every
// sound stops immediately.
const MUTE_KEY = "dial_audio_muted";
let ctx, master, timer, step = 0;
let playing = false, armed = false;
let muted = (() => { try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; } })();
const listeners = new Set();

// An original cheerful pentatonic loop (frequencies in Hz; null = rest).
const SEQ = [
  523, 659, 784, 659, 587, 784, 988, 784,
  523, 659, 784, 880, 784, 659, 523, null,
  440, 523, 659, 523, 494, 659, 784, 659,
  523, 659, 784, 1046, 784, 659, 523, null,
];

function notify() { listeners.forEach((f) => f({ playing, muted })); }
if (typeof window !== "undefined") window.__dialAudioState = () => ({ playing, muted, context: ctx ? ctx.state : "none" });

export function subscribeSong(fn) {
  listeners.add(fn);
  fn({ playing, muted });
  return () => listeners.delete(fn);
}
export const subscribeAudio = subscribeSong;
export const isMuted = () => muted;

function ensureCtx() {
  if (ctx) return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = muted ? 0 : 1;
  master.connect(ctx.destination);
}

function tone(freq, { type = "square", vol = 0.05, len = 0.17 } = {}) {
  if (!ctx || !master || muted) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.value = freq;
  const t = ctx.currentTime;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t + len);
  o.connect(g); g.connect(master);
  o.start(t); o.stop(t + len + 0.01);
}

function playStep() {
  const f = SEQ[step % SEQ.length];
  step++;
  if (f) tone(f);
}

export function startSong() {
  if (muted) return; // muted: never start
  ensureCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume();
  if (playing) return;
  playing = true; step = 0;
  timer = setInterval(playStep, 190);
  notify();
}

export function stopSong() {
  if (timer) clearInterval(timer);
  timer = null;
  if (playing) { playing = false; notify(); }
}

// One global mute for every skin sound. Muting stops the song outright and
// suspends the audio context; unmuting never auto-starts the song.
export function setMuted(next) {
  muted = !!next;
  try { localStorage.setItem(MUTE_KEY, muted ? "1" : "0"); } catch { /* ignore */ }
  if (master) master.gain.value = muted ? 0 : 1;
  if (muted) {
    stopSong();
    if (ctx && ctx.state === "running") ctx.suspend();
  } else if (ctx && ctx.state === "suspended") {
    ctx.resume();
  }
  notify();
  return muted;
}

export function toggleMuteSong() { return setMuted(!muted); }

// Short original "tick" for wheel movement; a deeper "tock" for select.
export function playClick(kind = "tick") {
  if (muted) return;
  ensureCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume();
  if (kind === "select") tone(660, { type: "triangle", vol: 0.12, len: 0.06 });
  else tone(1800, { type: "square", vol: 0.025, len: 0.018 });
}

// Browsers block audio until a user gesture. Arm a one-time listener so the
// loop kicks in on the first click/tap after the skin loads (unless muted).
export function armAutoplay() {
  if (armed) { startSong(); return; }
  armed = true;
  startSong();
  const kick = (e) => {
    window.removeEventListener("pointerdown", kick);
    window.removeEventListener("keydown", kick);
    // Don't let a tap on the mute control itself start the song.
    if (e?.target?.closest?.("[data-audio-control]")) return;
    startSong();
  };
  window.addEventListener("pointerdown", kick);
  window.addEventListener("keydown", kick);
}
